// Shared species configuration for the entire application.
//
// Naming convention: COMMON names are the SSOT here. The scientific name
// stays in the human-readable label only. This matches the backend's
// `SUPPORTED_SPECIES` (config.py) and what's stored in MongoDB across the
// growth_records / prognosis_records / spreadsheet_records collections.

// Currently supported species (selectable across the platform).
// NOTE: Tripletail is intentionally NOT here — it's gated to allow-listed
// users via `GET /api/user-species` and injected per-page through the
// `userSpecies.filter(s => s.value === 'Tripletail')` pattern. Adding it
// here would expose it to every user.
export const SUPPORTED_SPECIES = [
  { value: "Seabream", label: "Gilthead seabream (Sparus aurata)", group: "Mediterranean" },
  { value: "Seabass", label: "European seabass (Dicentrarchus labrax)", group: "Mediterranean" },
  { value: "Tilapia", label: "Nile tilapia (Oreochromis niloticus)", group: "Freshwater" },
];

// Coming soon species (visible but disabled)
export const COMING_SOON_SPECIES = [
  // Salmonids
  { value: "Salmo salar", label: "Salmon (Salmo salar)", group: "Salmonids" },
  { value: "Oncorhynchus mykiss", label: "Rainbow trout (Oncorhynchus mykiss)", group: "Salmonids" },
  { value: "Salvelinus alpinus", label: "Arctic char (Salvelinus alpinus)", group: "Salmonids" },
  // Amberjacks
  { value: "Seriola dumerili", label: "Greater amberjack (Seriola dumerili)", group: "Amberjacks" },
  { value: "Seriola quinqueradiata", label: "Yellowtail amberjack (Seriola quinqueradiata)", group: "Amberjacks" },
  // Other Marine
  { value: "Argyrosomus regius", label: "Meagre (Argyrosomus regius)", group: "Marine" },
  { value: "Rachycentron canadum", label: "Cobia (Rachycentron canadum)", group: "Marine" },
  { value: "Lates calcarifer", label: "Barramundi / Asian seabass (Lates calcarifer)", group: "Marine" },
  { value: "Gadus morhua", label: "Atlantic cod (Gadus morhua)", group: "Marine" },
  { value: "Hippoglossus hippoglossus", label: "Atlantic halibut (Hippoglossus hippoglossus)", group: "Marine" },
];

// All species combined for display
export const ALL_SPECIES = [...SUPPORTED_SPECIES, ...COMING_SOON_SPECIES];

// Helper to check if a species is coming soon
export const isComingSoonSpecies = (value) => {
  return COMING_SOON_SPECIES.some(s => s.value === value);
};

// Helper to check if species is Tilapia.
// Accepts the canonical value ("Tilapia") AND legacy scientific spellings
// ("Oreochromis niloticus") so old saved records still resolve correctly.
export const isTilapiaSpecies = (species) => {
  const speciesLower = (species || '').toLowerCase();
  return speciesLower.includes('tilapia') || speciesLower.includes('oreochromis');
};

// Helper to check if species is Seabass (European Seabass — Dicentrarchus
// labrax). NOTE: guarded against false positives from "Asian seabass"
// (Barramundi/Lates calcarifer) which is a separate coming-soon species.
export const isSeabassSpecies = (species) => {
  const s = (species || '').toLowerCase();
  if (s.includes('lates') || s.includes('barramundi') || s.includes('asian')) return false;
  return s.includes('seabass') || s.includes('sea bass') || s.includes('dicentrarchus');
};

// Helper to check if species is Seabream
export const isSeabreamSpecies = (species) => {
  const speciesLower = (species || '').toLowerCase();
  return speciesLower.includes('seabream') || speciesLower.includes('sparus');
};

// Helper to check if species is Tripletail (restricted access)
export const isTripletailSpecies = (species) => {
  const speciesLower = (species || '').toLowerCase();
  return speciesLower.includes('tripletail') || speciesLower.includes('lobotes');
};

// Coming soon message
export const COMING_SOON_MESSAGE = "This species is coming soon and is not yet available for analysis.";
